/**
 * @file api.ts
 * @brief WEBPANEL client for address autocomplete (Geoapify via backend proxy)
 *
 * @details
 * Calls `GET /api/address/autocomplete` so `GEOAPIFY_API_KEY` stays server-side.
 * Same contract as mobile `FRONTEND/src/features/address/api.ts`.
 *
 * @dependencies
 * - shared/api/client
 * - shared/api/endpoints
 *
 * @references
 * - BACKEND/src/features/address/routes.js
 * - components/ui/AddressAutocomplete.tsx
 *
 * @author DES
 * @date 27-07-2026
 */

import { apiClient } from "@/shared/api/client";
import { addressEndpoints } from "@/shared/api/endpoints";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------

export type AddressSuggestion = {
  id: string;
  label: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  /** ISO 3166-1 alpha-2 uppercase */
  countryCode: string;
};

type AutocompleteResponse = {
  success: boolean;
  data?: { suggestions: AddressSuggestion[] };
  message?: string;
  code?: string;
};

// ----------------------------------------------------------------------
// API
// ----------------------------------------------------------------------

export async function fetchAddressSuggestions(
  query: string,
  opts?: { limit?: number; country?: string },
): Promise<AddressSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) return [];

  const { data } = await apiClient.get<AutocompleteResponse>(
    addressEndpoints.autocomplete,
    {
      params: {
        q,
        limit: opts?.limit ?? 10,
        ...(opts?.country ? { country: opts.country } : {}),
      },
    },
  );

  return data?.data?.suggestions ?? [];
}
